import { useState, KeyboardEvent, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(
  function ChatInput({ onSend, disabled }, ref) {
    const { t } = useTranslation('common')
    const [input, setInput] = useState('')

    const handleSend = () => {
      if (input.trim() && !disabled) {
        onSend(input)
        setInput('')
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    return (
      <div className="border-t border-brand-dark/10 px-4 py-3 bg-white">
        <div className="flex gap-2 items-center">
          <input
            ref={ref}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.placeholder')}
            disabled={disabled}
            className={cn(
              'flex-1 px-4 py-2.5 text-sm bg-white text-brand-text',
              'border border-brand-dark/15 rounded-md',
              'focus:outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/20',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'placeholder:text-brand-text/40 transition-colors'
            )}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || !input.trim()}
            className={cn(
              'group relative px-3 py-2.5 rounded-md flex-shrink-0',
              'bg-brand-red text-white',
              'hover:bg-brand-red-dark transition-colors',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-brand-red',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-1'
            )}
            aria-label={t('chatbot.send')}
          >
            <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-disabled:translate-x-0" />
          </button>
        </div>
      </div>
    )
  }
)
